// @flow

import { connect } from 'react-redux';
import styled, { css } from 'styled-components';
import {
  TASK_INSTANCE_COMPLETED_STATUS,
  TASK_INSTANCE_PENDING_STATUS
} from 'sling-calendar-sdk/constants';
import canInteractWithSubtasks from 'sling-calendar-sdk/helpers/shiftTasks/canInteractWithSubtasks';

import type {
  TaskInstance,
  Subtask,
  User,
  UpdateSubtaskStatusActionCreator
} from '../common/typings';
import ContentText from '../common/components/Text/ContentText';
import selectedIcon from '../assets/svg/icons/selected-filled.svg';
import selectedGreyIcon from '../assets/svg/icons/selected-filled-gray.svg';
import unselectedIcon from '../assets/svg/icons/unselected.svg';
import LabeledCheckbox from '../common/components/ui/LabeledCheckbox';

import { updateSubtaskStatus } from './actions';

const ROW_HEIGHT = '36px';

const Container = styled.div`
  opacity: ${props => (props.isCompleted ? 0.65 : 1)};
  cursor: ${props => (props.canInteract ? 'default' : 'not-allowed')};
  margin-right: 8px;
  min-height: ${ROW_HEIGHT};
`;

const rowCss = css`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-start;
  min-height: ${ROW_HEIGHT};
`;

const SubtaskRow = styled.div`
  padding-left: 10px;
  ${rowCss}
`;

const Icon = styled.img`
  margin-right: 8px;
  padding-top: 9px;
`;

const SubtaskTitleText = styled(ContentText)`
  padding-top: 5px;
  padding-bottom: 5px;
`;

type Props = {
  user: User | null,
  taskInstance: TaskInstance,
  subtask: Subtask,
  updateSubtaskStatus: UpdateSubtaskStatusActionCreator
};

export function SubtaskComponent({ user, taskInstance, subtask, updateSubtaskStatus }: Props) {
  const isSubtaskCompleted = subtask.status === TASK_INSTANCE_COMPLETED_STATUS;
  const isTaskCompleted = taskInstance.status === TASK_INSTANCE_COMPLETED_STATUS;

  const getIcon = () => {
    if (isSubtaskCompleted) {
      return isTaskCompleted ? selectedGreyIcon : selectedIcon;
    }
    return unselectedIcon;
  };

  const canInteract = Boolean(user && canInteractWithSubtasks(taskInstance, user));

  if (!canInteract) {
    return (
      <Container isCompleted={isTaskCompleted} canInteract={false}>
        <SubtaskRow>
          <Icon src={getIcon()} />
          <SubtaskTitleText>{subtask.title}</SubtaskTitleText>
        </SubtaskRow>
      </Container>
    );
  }

  const toggleSubtask = (isSelected: boolean) => {
    const status = isSelected ? TASK_INSTANCE_COMPLETED_STATUS : TASK_INSTANCE_PENDING_STATUS;
    updateSubtaskStatus(taskInstance, subtask.id, status);
  };

  return (
    <Container isCompleted={isTaskCompleted}>
      <LabeledCheckbox
        initialValue={isSubtaskCompleted}
        label={subtask.title}
        onToggle={toggleSubtask}
        withPadding
      />
    </Container>
  );
}

export default connect(null, { updateSubtaskStatus })(SubtaskComponent);
